import { useRef, useState } from "react";
import { Throwable } from "@wss/error-tracking/throwable";
import {
  EnrollmentOption,
  PlusPotentialSavingsInfo,
  ShippingAddress,
} from "../../../../models";
import Errors from "../../components/Errors";
import Footer from "../../components/Footer";
import PaymentSelection from "../../components/PaymentSelection";
import { PaymentSelectionRef } from "../../../paymentSelectionRefs";
import { ModalState } from "../modalState";
import {
  submitCancelledSubscriptionReinstateRequest,
  submitNewSubscriptionEnrollmentRequest,
} from "../utils";
import WebPlusPaymentDetails from "../../components/WebPlusPaymentDetails";

interface RestoreSubscriptionFormProps {
  formattedPlusPrice: string;
  address: ShippingAddress;
  handleShowWelcome: () => void;
  plusPotentialSavingsInfo: PlusPotentialSavingsInfo | undefined;
  enrollmentOptions: EnrollmentOption[] | readonly EnrollmentOption[];
  modalState: ModalState;
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>;
  subscriptionChargeDate?: Date;
  isOpenFromAd?: boolean;
  isPendingCancellation?: boolean;
}

const RestoreSubscriptionForm = ({
  formattedPlusPrice,
  handleShowWelcome,
  plusPotentialSavingsInfo,
  enrollmentOptions,
  modalState,
  setModalState,
  subscriptionChargeDate,
  isOpenFromAd = false,
  address,
  isPendingCancellation = false,
}: RestoreSubscriptionFormProps) => {
  const paymentSelectionRef = useRef<PaymentSelectionRef>(null);

  const defaultCard =
    modalState.savedCardData !== undefined
      ? (modalState.savedCardData.filter((element) => element.default)[0] ??
        modalState.savedCardData[0])
      : undefined;
  const [savedCardId, setSavedCardId] = useState<number | undefined>(
    defaultCard?.num,
  );

  const showEnrollmentOptions =
    enrollmentOptions.length > 1 &&
    !isPendingCancellation &&
    enrollmentOptions.some(
      (option) => option.promotionName?.toLowerCase() === "first month free",
    );

  const submitForm = async (): Promise<Throwable<void>> => {
    if (modalState.shippingId === 0) {
      setModalState((prev) => ({
        ...prev,
        subscribeCustomErrorMessage: "Please select a shipping address",
      }));
      const error = new Error("A shipping address must be selected");
      return {
        error,
        isError: true,
      };
    }
    setModalState((prev) => ({
      ...prev,
      submitting: true,
      subscribeCustomErrorMessage: "",
    }));
    if (!paymentSelectionRef) {
      return {
        isError: false,
      };
    }

    const tokenizeResult = await paymentSelectionRef.current?.tokenize();
    if (tokenizeResult?.suggestVerifyInformation) {
      setModalState((prev) => ({
        ...prev,
        cardVerificationError: true,
        submitting: false,
      }));
    } else if (!tokenizeResult?.isTokenized) {
      setModalState((prev) => ({
        ...prev,
        cardSaveError: true,
        submitting: false,
      }));
    } else if (isPendingCancellation) {
      return submitCancelledSubscriptionReinstateRequest(
        modalState,
        setModalState,
        plusPotentialSavingsInfo,
        isOpenFromAd,
        handleShowWelcome,
      );
    } else {
      return submitNewSubscriptionEnrollmentRequest(
        modalState,
        setModalState,
        plusPotentialSavingsInfo,
        isOpenFromAd,
        handleShowWelcome,
        tokenizeResult.tokenId,
      );
    }

    return {
      isError: false,
    };
  };

  const formattedRenewalDate = subscriptionChargeDate
    ? new Date(subscriptionChargeDate.toString()).toLocaleDateString("en-US", {
        timeZone: "UTC",
      })
    : "";

  return (
    <div
      className="px-6 pb-6 xs:px-10 lm:px-12 md:pb-10 lm:w-[393px]"
      data-testid="restore-subscription-form"
    >
      <div className="flex font-bold py-6 lm:pt-10 border-0 border-b border-solid border-gray-200 text-gray-900 text-2xl lm:text-3xl lm:text-center">
        Restore Plus Subscription
      </div>
      <div className="px-0 py-6 border-0 border-b border-solid border-gray-200">
        <div className="lm:flex lm:flex-col">
          <span
            id="plus-modal-shipping-heading"
            className="w-auto text-base font-bold text-gray-800"
          >
            Shipping Address
          </span>
          <p
            id="plus-restore-modal-shipping-information"
            className="text-base leading-5 font-normal text-gray-800 pt-2 m-0"
            data-testid="restore-shipping-address"
          >
            {address.display}
          </p>
        </div>
      </div>
      {showEnrollmentOptions ? (
        <WebPlusPaymentDetails
          enrollmentOptions={enrollmentOptions as EnrollmentOption[]}
          formattedPlusPrice={formattedPlusPrice}
          setPromotionName={(newPromotionName) =>
            setModalState((prev) => ({
              ...prev,
              promotionName: newPromotionName,
            }))
          }
          promotionName={modalState.promotionName}
          subscriptionChargeDate={subscriptionChargeDate}
        />
      ) : (
        <div className="px-0 py-6 border-0 border-b border-solid border-gray-200">
          <div className="lm:flex lm:flex-col">
            <span
              id="plus-modal-shipping-heading"
              className="w-auto text-base font-bold text-gray-800"
            >
              Payment Details
            </span>
            <p
              id="plus-restore-modal-payment-information"
              className="text-base leading-5 font-normal text-gray-800 pt-2 m-0"
              data-testid="restore-payment-details"
            >
              {formattedPlusPrice}/month beginning {formattedRenewalDate}
            </p>
          </div>
        </div>
      )}
      <div className="px-0 pt-6 pb-1 border-0 border-b border-solid border-gray-200">
        <fieldset className="p-0 border-0 lm:flex-col">
          <legend className="w-full p-0 text-base font-bold text-gray-700">
            Payment Method
          </legend>
          <PaymentSelection
            ref={paymentSelectionRef}
            setSavedCardId={setSavedCardId}
            defaultTokenId={savedCardId}
            defaultSavedCards={modalState.savedCardData.map((element) => ({
              id: element.num,
              type: element.creditCardType,
              mask: element.lastFour,
              name: element.name,
              isRewardsCard: element.isRewardsCard,
              isDefault: element.default,
            }))}
          />
        </fieldset>
        <Errors
          cardVerificationError={modalState.cardVerificationError}
          cardSaveError={modalState.cardSaveError}
          subscribeError={modalState.subscribeError}
          subscribeCustomErrorMessage={modalState.subscribeCustomErrorMessage}
        />
      </div>
      <Footer
        submitting={modalState.submitting}
        submitForm={submitForm}
        buttonText="Restore Subscription"
      />
    </div>
  );
};
export default RestoreSubscriptionForm;
